import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { MultiQuickFilterSelection } from '@app/catalog-plan/models/catalog-plan.model';
import { CatalogPlanBlockViewService } from '@app/catalog-plan/services/catalog-plan-blockView.service';
import { CatalogPlanBlockViewCommunicationService } from '@app/catalog-plan/services/catalog-plan-blockview-communication.service';
import { CatalogPlanTopviewService } from '@app/catalog-plan/services/catalog-plan-topview.service';
import { CatalogPlanService } from '@app/catalog-plan/services/catalog-plan.service';
import { FeatureFlags } from '@core/feature-flags';
import { AlertService } from '@core/services/alert.service';
import {
  CatalogPlanEventTypeEnumResponseDto,
  EventSessionMultiSelectionStatsResponseDto,
  EventSessionPlanBlockFilterRequestDto,
  EventSessionPlanBlockFilterResponseDto,
  EventSessionPlanBlockResponseDto,
  EventSessionPlanCategoryResponseDto,
  EventSessionPlanContingentResponseDto,
  EventSessionPlanUnavailableSeatTypesResponseDto,
  EventSessionStatsResponseDto,
  EventSessionVenueInformationLegendResponseDto,
} from '@core/services/apiclient';
import { FeatureFlagService } from '@core/services/feature-flag.service';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { select, Store } from '@ngrx/store';
import * as fromCatalogPlan from '@root-store/catalog-plan-store/index';
import { combineLatest, concat, forkJoin, Observable, of } from 'rxjs';
import { catchError, concatMap, filter, map, switchMap, withLatestFrom } from 'rxjs/operators';
import * as CatalogPlanActions from '../actions/catalog-plan.actions';

@Injectable()
export class CatalogPlanEffects {
  private readonly actions = inject(Actions);
  private readonly router = inject(Router);
  private readonly store = inject<Store<fromCatalogPlan.State>>(Store);
  private readonly service = inject(CatalogPlanService);
  private readonly communicationService = inject(CatalogPlanBlockViewCommunicationService);
  private readonly topViewService = inject(CatalogPlanTopviewService);
  private readonly eventBlockViewService = inject(CatalogPlanBlockViewService);
  private readonly alertService = inject(AlertService);

  private readonly featureFlagService = inject(FeatureFlagService);

  // Lu a chaque passage et non memorise a la construction : les flags arrivent en asynchrone, un
  // champ initialise ici serait fige sur la valeur par defaut.
  private isStatusTabEnabled(): boolean {
    return this.featureFlagService.isEnabledSync(FeatureFlags.PLANDUCATALOGUE_ONGLETSTATUT, false);
  }

  resetPlan$ = createEffect(() => {
    return this.actions.pipe(
      ofType(fromCatalogPlan.actions.resetPlan),
      withLatestFrom(this.store.select(fromCatalogPlan.selectors.selectSourcePage)),
      concatMap(([action, sourcePage]) => {
        if (sourcePage === 'dashboard') {
          this.router.navigate(['dashboard'], { skipLocationChange: true });
        } else {
          this.router.navigate(['catalog', 'events'], { skipLocationChange: true });
        }

        return [{ type: 'Empty Action' }];
      }),
    );
  });

  initPlanView$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.initPlanView),
      concatMap(({ eventSessionId, planType }) => {
        return [
          CatalogPlanActions.setEventSessionId({ eventSessionId }),
          CatalogPlanActions.setPlanType({ planType }),
          CatalogPlanActions.loadPlanData({}),
        ];
      }),
    );
  });

  reloadPlanView$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.reloadPlanView),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectPlanIds)),
      ),
      switchMap(([action, blockId, planType]) => {
        const initialLoadActions = [
          CatalogPlanActions.loadContingentsFilterData(),
          CatalogPlanActions.loadCategoriesFilterData(),
          CatalogPlanActions.loadUnavailableSeatTypesFilterData(),
          CatalogPlanActions.loadAllCategories({ blockId }),
          CatalogPlanActions.loadAllContingents({ blockId }),
          CatalogPlanActions.loadAllUnavailableSeats({ blockId }),
          CatalogPlanActions.loadViewSeatStatusStats({ blockId }),
          CatalogPlanActions.resetMultiQuickFilter(),
        ];

        if (planType.planType === CatalogPlanEventTypeEnumResponseDto.MultiBlock) {
          return blockId
            ? of(
                ...initialLoadActions,
                CatalogPlanActions.refreshCatalogBlockViewSeatsData({ blockId }),
                CatalogPlanActions.refreshTopViewMinimapAvailabilities(),
              )
            : of(...initialLoadActions, CatalogPlanActions.loadPlanData({ blockId }));
        }

        return of(
          ...initialLoadActions,
          CatalogPlanActions.refreshCatalogExcludingMultiBlocksViewSeatsData(),
        );
      }),
    );
  });

  refreshCatalogBlockViewSeatsData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.refreshCatalogBlockViewSeatsData),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatsStampVersion)),
      ),
      switchMap(([action, sessionId, blockId, stampVersion]) => {
        return this.service.getDifferentialBlockViewSeats(sessionId, blockId, stampVersion).pipe(
          concatMap(([seats, stampVersion]) => {
            this.eventBlockViewService.refreshBlockViewSeats(seats);
            return [
              CatalogPlanActions.refreshCatalogBlockViewSeatsDataSuccess({
                seatsStampVersion: stampVersion,
              }),
            ];
          }),
          catchError(() => of({ type: 'EMPTY' })),
        );
      }),
    );
  });

  refreshCatalogExcludingMultiBlocksViewSeatsData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.refreshCatalogExcludingMultiBlocksViewSeatsData),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatsStampVersion)),
      ),
      switchMap(([action, sessionId, stampVersion]) => {
        return this.service.getDifferentialExcludingMultiBlocksSeats(sessionId, stampVersion).pipe(
          concatMap(([seats, stampVersion]) => {
            this.eventBlockViewService.refreshBlockViewSeats(seats);
            return [
              CatalogPlanActions.refreshCatalogExcludingMultiBlocksSeatsSuccess({
                seatsStampVersion: stampVersion,
              }),
            ];
          }),
          catchError(() => of({ type: 'EMPTY' })),
        );
      }),
    );
  });

  loadPlanData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadPlanData),
      withLatestFrom(this.store.pipe(select(fromCatalogPlan.selectors.selectPlanIds))),
      switchMap(([action, { eventSessionId, planType, blockId }]) => {
        if (planType === CatalogPlanEventTypeEnumResponseDto.MultiBlock && !!blockId) {
          return [CatalogPlanActions.loadBlockViewData({ blockId, eventSessionId })];
        } else if (planType === CatalogPlanEventTypeEnumResponseDto.MultiBlock && !blockId) {
          return forkJoin([
            this.service.getCatalogEventDetails(eventSessionId),
            this.service.getCatalogEventBackGround(eventSessionId),
            this.service.getTopviewPlanBlocks(eventSessionId),
            this.service.getAllContingentsImages(),
          ])
            .pipe(
              map(([eventDetails, planBackground, planData, allContingentImages]) => ({
                eventDetails,
                planBackground,
                planData,
                allContingentImages,
                blockViewData: null,
              })),
            )
            .pipe(
              concatMap(({ eventDetails, planBackground, planData, allContingentImages, blockViewData }) => {
                if (planData.length == 0) {
                  this.alertService.showCustom(`catalogPlan.planNotFound`, 'info');
                  return of({ type: 'EMPTY' });
                }

                this.router.navigate(['catalog', 'catalog-plan'], {
                  skipLocationChange: true,
                });

                return [
                  CatalogPlanActions.loadTopViewPlanDataSuccess({
                    eventDetails,
                    planBackground,
                    planData,
                    allContingentImages,
                  }),
                  CatalogPlanActions.setBlockViewData({ blockViewData }),
                  CatalogPlanActions.loadContingentsFilterData(),
                  CatalogPlanActions.loadCategoriesFilterData(),
                  CatalogPlanActions.loadUnavailableSeatTypesFilterData(),
                  CatalogPlanActions.loadAllCategories({}),
                  CatalogPlanActions.loadAllContingents({}),
                  CatalogPlanActions.loadAllUnavailableSeats({}),
                  CatalogPlanActions.loadViewSeatStatusStats({}),
                ];
              }),
              catchError((err: unknown) => {
                this.alertService.showApiError(err, { fallback: 'catalogPlan.planNotFound', type: 'info' });
                return of(CatalogPlanActions.loadTopViewPlanDataFailure());
              }),
            );
        } else {
          return [CatalogPlanActions.loadExcludingMultiBlocksPlan({ eventSessionId })];
        }
      }),
    );
  });

  loadBlockViewData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadBlockViewData),
      withLatestFrom(this.store.pipe(select(fromCatalogPlan.selectors.selectAllContingentImages))),
      switchMap(([{ eventSessionId, blockId }, allContingentImages]) => {
        const loadData$ = combineLatest([
          this.service.getCatalogEventDetails(eventSessionId),
          this.service.getBlockViewSeats(eventSessionId, blockId),
          this.service.getBlockViewLetters(eventSessionId, blockId),
          this.service.getBlockViewImages(eventSessionId, blockId),
          this.service.getBlockViewShapes(eventSessionId, blockId),
          of(allContingentImages),
        ]).pipe(
          map(
            ([
              eventDetails,
              [seats, stampVersion],
              lettersResponse,
              imageData,
              shapesResponse,
              allContingentImages,
            ]) => ({
              eventDetails: eventDetails,
              seatsStampVersion: stampVersion,
              blockViewData: {
                blockId: blockId,
                seats: seats,
                letters: lettersResponse,
                contingents: allContingentImages,
                shapes: shapesResponse,
                imageData: imageData,
              },
              allContingentImages,
              blockId: blockId,
            }),
          ),
        );

        return loadData$.pipe(
          concatMap(({ eventDetails, blockViewData, seatsStampVersion, blockId }) => {
            this.router.navigate(['catalog', 'catalog-plan'], {
              skipLocationChange: true,
            });
            return [
              CatalogPlanActions.loadBlockViewDataSuccess({
                eventDetails,
                blockViewData,
                seatsStampVersion,
              }),
              CatalogPlanActions.loadAllCategories({ blockId: blockId }),
              CatalogPlanActions.loadAllContingents({ blockId: blockId }),
              CatalogPlanActions.loadAllUnavailableSeats({ blockId: blockId }),
              CatalogPlanActions.loadViewSeatStatusStats({ blockId: blockId }),
            ];
          }),
          catchError((err: unknown) => {
            this.alertService.showApiError(err, { fallback: 'catalogPlan.planNotFound', type: 'info' });
            return of(CatalogPlanActions.loadBlockViewDataFailure());
          }),
        );
      }),
    );
  });

  loadExcludingMultiBlocksPlan$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadExcludingMultiBlocksPlan),
      switchMap(({ eventSessionId }) => {
        const loadData$ = combineLatest([
          this.service.getExcludingMultiBlocksSeats(eventSessionId),
          this.service.getCatalogEventDetails(eventSessionId),
          this.service.getAllContingentsImages(),
          this.service.getExcludingMultiBlocksLetters(eventSessionId),
          this.service.getExcludingMultiBlocksImages(eventSessionId),
          this.service.getExcludingMultiBlocksShapes(eventSessionId),
        ]).pipe(
          map(
            ([
              [seats, stampVersion],
              eventDetails,
              allContingentImages,
              lettersResponse,
              imageData,
              shapesResponse,
            ]) => ({
              eventDetails: eventDetails,
              blockViewData: {
                blockId: null,
                seats: seats,
                letters: lettersResponse,
                contingents: allContingentImages,
                shapes: shapesResponse,
                imageData: imageData,
              },
              seatsStampVersion: stampVersion,
            }),
          ),
        );
        return loadData$.pipe(
          concatMap(({ eventDetails, blockViewData, seatsStampVersion }) => {
            this.router.navigate(['catalog', 'catalog-plan'], {
              skipLocationChange: true,
            });
            return [
              CatalogPlanActions.loadExcludingMultiBlocksPlanSuccess({
                eventDetails: eventDetails,
                blockViewData: blockViewData,
                seatsStampVersion: seatsStampVersion,
              }),
              CatalogPlanActions.loadContingentsFilterData(),
              CatalogPlanActions.loadCategoriesFilterData(),
              CatalogPlanActions.loadUnavailableSeatTypesFilterData(),
            ];
          }),
          catchError((err: unknown) => {
            this.alertService.showApiError(err, { fallback: 'catalogPlan.planNotFound', type: 'info' });
            return of(CatalogPlanActions.loadExcludingMultiBlocksPlanFailure());
          }),
        );
      }),
    );
  });

  loadLegendData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadLegendData),
      withLatestFrom(this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId))),
      switchMap(([action, eventSessionId]) =>
        this.service.getEventSessionVenueInformationLegend(eventSessionId).pipe(
          concatMap((data: EventSessionVenueInformationLegendResponseDto) => [
            CatalogPlanActions.setLegend({ legend: data }),
          ]),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  loadContingentsFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CatalogPlanActions.loadContingentsFilterData,
        CatalogPlanActions.updateCategoryIdsFilterSelected,
        CatalogPlanActions.updateUnavailableSeatTypeIdsFilterSelected,
        CatalogPlanActions.updateSeatStatusIdsFilterSelected,
        CatalogPlanActions.resetCatalogPlanFilters,
      ),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionPlanFilter)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatStatusIdsFilterSelected)),
      ),
      switchMap(([action, filter, seatStatuses]) =>
        this.service
          .getEventSessionPlanContingents(
            filter.eventSessionId,
            filter.categoriesFilter,
            filter.unavailableSeatTypesFilter,
            seatStatuses,
          )
          .pipe(
            concatMap((data: EventSessionPlanContingentResponseDto[]) => [
              CatalogPlanActions.updateContingentsFilterList({ contingentsFilterList: data }),
            ]),
            catchError(() => of(null)),
          ),
      ),
    );
  });

  loadCategoriesFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CatalogPlanActions.loadCategoriesFilterData,
        CatalogPlanActions.updateContingentIdsFilterSelected,
        CatalogPlanActions.updateUnavailableSeatTypeIdsFilterSelected,
        CatalogPlanActions.updateSeatStatusIdsFilterSelected,
        CatalogPlanActions.resetCatalogPlanFilters,
      ),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionPlanFilter)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatStatusIdsFilterSelected)),
      ),
      switchMap(([action, filter, seatStatuses]) =>
        this.service
          .getEventSessionPlanCategories(
            filter.eventSessionId,
            filter.contingentsFilter,
            filter.unavailableSeatTypesFilter,
            seatStatuses,
          )
          .pipe(
            concatMap((data: EventSessionPlanCategoryResponseDto[]) => [
              CatalogPlanActions.updateCategoriesFilterList({ categoriesFilterList: data }),
            ]),
            catchError(() => of(null)),
          ),
      ),
    );
  });

  loadUnavailableSeatTypesFilterData$ = createEffect(() => {
    return this.actions.pipe(
      ofType(
        CatalogPlanActions.loadUnavailableSeatTypesFilterData,
        CatalogPlanActions.updateContingentIdsFilterSelected,
        CatalogPlanActions.updateCategoryIdsFilterSelected,
        CatalogPlanActions.updateSeatStatusIdsFilterSelected,
        CatalogPlanActions.resetCatalogPlanFilters,
      ),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionPlanFilter)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatStatusIdsFilterSelected)),
      ),
      switchMap(([action, filter, seatStatuses]) =>
        this.service
          .getEventSessionPlanUnavailableSeatTYpes(
            filter.eventSessionId,
            filter.contingentsFilter,
            filter.categoriesFilter,
            seatStatuses,
          )
          .pipe(
            concatMap((data: EventSessionPlanUnavailableSeatTypesResponseDto[]) => [
              CatalogPlanActions.updateUnavailableSeatTypesFilterList({
                unavailableSeatTypesFilterList: data,
              }),
            ]),
            catchError(() => of(null)),
          ),
      ),
    );
  });

  loadFilteredSeatStatusStats$ = createEffect(() =>
    this.actions.pipe(
      ofType(
        CatalogPlanActions.loadContingentsFilterData,
        CatalogPlanActions.updateContingentIdsFilterSelected,
        CatalogPlanActions.updateCategoryIdsFilterSelected,
        CatalogPlanActions.updateUnavailableSeatTypeIdsFilterSelected,
        CatalogPlanActions.updateSeatStatusIdsFilterSelected,
        CatalogPlanActions.resetCatalogPlanFilters,
      ),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionPlanFilter)),
      ),
      switchMap(([action, filter]) =>
        this.service.getCatalogEventSessionMultiSelectionStats(
          filter.eventSessionId,
          filter.contingentsFilter,
          filter.categoriesFilter,
          filter.unavailableSeatTypesFilter,
          null,
          filter.seatStatusIdsFilter,
        ).pipe(
          map(response => CatalogPlanActions.updateFilteredSeatStatusStats({ filteredSeatStatusStats: response })),
          catchError(() => of(CatalogPlanActions.updateFilteredSeatStatusStats({ filteredSeatStatusStats: null }))),
        ),
      ),
    ),
  );

  applyPlanFilter$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.applyPlanFilter),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.applyPlanFilter)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, filter, blockId]) => {
        // Le top-view (plan complet en vue top, minimap en vue bloc) doit toujours refleter le
        // filtre principal applique/reinitialise. En vue bloc, la minimap n'existe que pour les
        // plans MultiBlock.
        const hasTopView =
          !blockId || filter.planType === CatalogPlanEventTypeEnumResponseDto.MultiBlock;
        if (!hasTopView) {
          return [{ type: 'Empty Action' }];
        }
        return this.applyTopViewFilter(filter).pipe(
          concatMap((data: EventSessionPlanBlockFilterResponseDto[]) => {
            this.topViewService.refreshBlockAvailabilities(data);
            return [{ type: 'Empty Action' }];
          }),
          catchError(() => of({ type: 'EMPTY' })),
        );
      }),
    );
  });

  applyPlanFilterAndSelect$ = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogPlanActions.applyPlanFilterAndSelect),
      withLatestFrom(this.store.pipe(select(fromCatalogPlan.selectors.selectPlanIds))),
      switchMap(([action, planIds]) =>
        this.service.getCatalogEventSessionMultiSelectionStats(
          planIds.eventSessionId,
          action.contingentIds,
          action.categoryIds,
          action.unavailableSeatTypeIds,
          planIds.blockId,
          action.seatStatusIds,
        ).pipe(
          concatMap(response => [
            // Gel du perimetre : ce sont les parametres exacts qui viennent de produire `response`.
            // La modification de masse devra porter sur CES places, pas sur celles du bloc ou
            // l'utilisateur se trouvera au moment de valider.
            CatalogPlanActions.updateBulkSelectionStats({
              bulkSelectionStats: response,
              scope: {
                eventSessionId: planIds.eventSessionId,
                contingentIds: action.contingentIds,
                categoryIds: action.categoryIds,
                unavailabilityIds: action.unavailableSeatTypeIds,
                blockId: planIds.blockId,
                seatStatusIds: action.seatStatusIds,
              },
            }),
            CatalogPlanActions.applyPlanFilterAndSelectSuccess({ bulkSelectionStats: response }),
          ]),
          catchError(() => of(CatalogPlanActions.updateBulkSelectionStats({ bulkSelectionStats: null }))),
        ),
      ),
    ),
  );

  // Stats filtrees du bloc dans lequel l'utilisateur vient d'entrer, tant qu'une selection en masse
  // est gelee.
  //
  // ⚠ Cet effet dispatchait `updateBulkSelectionStats` : il ECRASAIT donc la selection gelee a chaque
  // changement de bloc. Consequence, le compteur (« Liberer les 153 place(s) indisponibles ») suivait
  // le bloc courant au lieu de rester ancre sur la selection, alors que le reducer traite deja
  // `bulkSelectionStats != null` comme « une selection est gelee » (il protege `multiQuickFilter` du
  // reset et court-circuite `resetMultiQuickFilter`). L'effet contredisait cet invariant.
  //
  // L'appel est CONSERVE, et voici le cas precis qui le justifie — celui de l'annulation de selection.
  //
  // `catalog-plan-sidebar.component.ts` a DEUX chemins d'annulation, et ils ne se comportent pas
  // pareil :
  //
  //   - onCancelFooterFilter() -> resetCommonFilters(true) : vide la selection ET le filtre rapide.
  //     Le bandeau de recap repart alors sur `viewSeatStatusStats` (stats du bloc, NON filtrees), ce
  //     qui est correct puisqu'il n'y a plus de filtre.
  //   - onCancelFilter() -> resetCommonFilters(false) : ne vide QUE `bulkSelectionStats`. Le filtre
  //     global et le filtre rapide RESTENT actifs. Le bandeau afficherait donc des stats non filtrees
  //     alors qu'un filtre est applique : le recap ne refleterait pas la vue.
  //
  // C'est ce second chemin que cette tranche sert : elle porte la seule donnee « bloc courant + filtre
  // courant » du store. Aucune autre ne peut s'y substituer — `viewSeatStatusStats` est scopee bloc mais
  // NON filtree, `filteredSeatStatusStats` est filtree mais plan-wide (`blockId = null`). L'effet ecrit
  // donc dans une tranche dediee, sans jamais toucher a la selection gelee.
  //
  // ⚠ Etat actuel : la tranche n'a pas encore de LECTEUR. Brancher le bandeau dessus suppose de deriver
  // le compteur « disponibles » par soustraction, `EventSessionMultiSelectionStatsResponseDto` n'ayant
  // pas de champ `availableSeats` — cela change le sens du chiffre affiche, donc c'est un arbitrage
  // produit et non technique. Tant qu'il n'est pas tranche, l'appel est fait sans etre consomme : c'est
  // assume et documente ici, pas un oubli (cf. review MR !200).
  loadBlockScopedFilterStatsOnBlockEntry$ = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogPlanActions.loadBlockViewDataSuccess),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectBulkSelectionStats)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectPlanIds)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionPlanFilter)),
      ),
      filter(([, stats]) => !!stats),
      switchMap(([, , planIds, planFilter]) =>
        this.service.getCatalogEventSessionMultiSelectionStats(
          planIds.eventSessionId,
          planFilter.contingentsFilter,
          planFilter.categoriesFilter,
          planFilter.unavailableSeatTypesFilter,
          planIds.blockId,
          planFilter.seatStatusIdsFilter,
        ).pipe(
          map(response =>
            CatalogPlanActions.updateBlockScopedFilterStats({ blockScopedFilterStats: response }),
          ),
          catchError(() =>
            of(CatalogPlanActions.updateBlockScopedFilterStats({ blockScopedFilterStats: null })),
          ),
        ),
      ),
    ),
  );

  loadPlanDataSuccess$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadTopViewPlanDataSuccess),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.applyPlanFilter)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectMultiQuickFilterSelections)),
      ),
      switchMap(([action, filter, multiQuickFilter]) => {
        let resultFilter$: Observable<EventSessionPlanBlockFilterResponseDto[]> | null = null;
        const seatStatuses = filter.seatStatusesFilter;
        if (
          filter.contingentsFilter?.length > 0 ||
          filter.categoriesFilter?.length > 0 ||
          filter.unavailableSeatTypesFilter?.length > 0 ||
          filter.seatStatusesFilter?.length > 0
        ) {
          resultFilter$ = this.applyTopViewFilter(filter);
        } else if (
          !!multiQuickFilter?.selectedCategories?.length ||
          !!multiQuickFilter?.selectedContingents?.length ||
          !!multiQuickFilter?.selectedUnavailabilities?.length ||
          !!multiQuickFilter?.selectedStatuses?.length
        ) {
          resultFilter$ = this.applyTopViewMultiQuickFilter(filter.eventSessionId, multiQuickFilter, seatStatuses);
        }

        if (!resultFilter$) {
          return [{ type: 'Empty Action' }];
        }
        return resultFilter$.pipe(
          map((filteredBlocks: EventSessionPlanBlockFilterResponseDto[]) =>
            action.planData.map((blockData) => {
              return this.refreshBlockAvailableSeats(blockData, filteredBlocks);
            }),
          ),
          map((planBlockData) => CatalogPlanActions.reloadPlanFilterDataSuccess({ planData: planBlockData })),
          catchError(() => of(null)),
        );
      }),
    );
  });

  loadAllContingents$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadAllContingents),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, eventSessionId, blockId]) =>
        this.service.getAllContingents(eventSessionId, blockId).pipe(
          concatMap((data: EventSessionPlanContingentResponseDto[]) => {
            const filteredData = blockId ? data.filter((item) => item.nbAvailableSeats > 0) : data;
            return [
              blockId
                ? CatalogPlanActions.updateBlockContingents({ blockContingents: filteredData })
                : CatalogPlanActions.updateAllContingents({ allContingents: filteredData }),
            ];
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  loadAllCategories$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadAllCategories),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, eventSessionId, blockId]) =>
        this.service.getAllCategories(eventSessionId, blockId).pipe(
          concatMap((data: EventSessionPlanCategoryResponseDto[]) => {
            const filteredData = blockId ? data.filter((item) => item.nbAvailableSeats > 0) : data;
            return [
              blockId
                ? CatalogPlanActions.updateBlockCategories({ blockCategories: filteredData })
                : CatalogPlanActions.updateAllCategories({ allCategories: filteredData }),
            ];
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  loadAllUnavailableSeats$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadAllUnavailableSeats),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, eventSessionId, blockId]) =>
        this.service.getAllUnavailableSeatsList(eventSessionId, blockId).pipe(
          concatMap((data: EventSessionPlanUnavailableSeatTypesResponseDto[]) => {
            const filteredData = blockId ? data.filter((item) => item.nbAvailableSeats > 0) : data;
            return [
              blockId
                ? CatalogPlanActions.updateBlockUnavailableSeats({ blockUnavailableSeats: filteredData })
                : CatalogPlanActions.updateAllUnavailableSeats({ allUnavailableSeats: filteredData }),
            ];
          }),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  loadStats$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.loadViewSeatStatusStats),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
      ),
      switchMap(([action, eventSessionId, blockId]) =>
        combineLatest([
          this.service.getCatalogEventSessionStats(eventSessionId, blockId),
          this.service.getCatalogEventSessionStats(eventSessionId, null),
          // Stats multi-selection non filtrees du perimetre courant : seules a porter
          // totalSelectedSeats, donc necessaires pour deduire le nombre de places libres de la
          // meme facon que le filtre global (cf. CatalogPlanUtilsService.computeFreeSeats).
          //
          // Conditionne au feature flag : cette donnee n'est consommee que par l'onglet Statut du
          // filtre rapide. loadViewSeatStatusStats est dispatche a l'init de la vue, au changement
          // de bloc et a chaque reloadPlanView : sans ce garde-fou on paierait une agregation sur
          // TOUT le perimetre (plusieurs dizaines de milliers de places) a chaque passage, pour
          // rien tant que le flag n'est pas ouvert.
          //
          // Echec ISOLE : combineLatest n'emet rien si une source erre, un 500 sur ce seul appel
          // priverait donc la vue des 2 autres jeux de stats. On emet null (meme convention que
          // updateFilteredSeatStatusStats / updateBulkSelectionStats) : l'onglet Statut affiche des
          // compteurs vides, le reste de la vue reste alimente.
          this.isStatusTabEnabled()
            ? this.service
                .getCatalogEventSessionMultiSelectionStats(eventSessionId, [], [], [], blockId, [])
                .pipe(catchError(() => of(null)))
            : of(null),
        ]).pipe(
          concatMap(([blockStats, planWideStats, viewMultiSelectionStats]: [
            EventSessionStatsResponseDto,
            EventSessionStatsResponseDto,
            EventSessionMultiSelectionStatsResponseDto | null,
          ]) => [
            CatalogPlanActions.updateViewSeatStatusStats({ viewSeatStatusStats: blockStats }),
            CatalogPlanActions.updatePlanWideSeatStatusStats({ planWideSeatStatusStats: planWideStats }),
            CatalogPlanActions.updateViewMultiSelectionStats({ viewMultiSelectionStats }),
          ]),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  performAllActions$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.initializeFullPlanView),
      concatMap(() =>
        concat(of(CatalogPlanActions.reloadPlanView()), of(CatalogPlanActions.resetSeatSelection())),
      ),
    );
  });

  resetSeatSelection$ = createEffect(() => {
    return this.actions.pipe(
      ofType(CatalogPlanActions.resetSeatSelection),
      switchMap(() =>
        of(this.communicationService.resetSeatSelection()).pipe(
          map(() => CatalogPlanActions.resetSeatSelectionSuccess()),
          catchError(() => of(null)),
        ),
      ),
    );
  });

  updateMultiQuickFilter$ = createEffect(() =>
    this.actions.pipe(
      ofType(fromCatalogPlan.actions.updateMultiQuickFilter),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectEventSessionId)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectPlanData)),
        this.store.pipe(select(fromCatalogPlan.selectors.selectSeatStatusIdsFilterSelected)),
      ),
      concatMap(([{ multiQuickFilter }, eventSessionId, planData, seatStatuses]) => {
        return this.applyTopViewMultiQuickFilter(eventSessionId, multiQuickFilter, seatStatuses).pipe(
          concatMap((data: EventSessionPlanBlockFilterResponseDto[]) => {
            this.topViewService.refreshBlockAvailabilities(data);
            return [{ type: 'Empty Action' }];
          }),
          catchError(() => of({ type: 'EMPTY' })),
        );
      }),
    ),
  );

  // Apres une validation de modif en masse (ou tout reload en vue bloc), le filtre rapide est
  // reinitialise mais le filtre principal, lui, persiste : la minimap top-view doit refleter le
  // MEME filtre que la vue bloc. On rappelle donc l'API top-view avec le filtre principal courant
  // (tableaux vides => non filtre => reset), et non un filtre vide force (GEN-6132).
  refreshTopViewMinimapAvailabilities$ = createEffect(() =>
    this.actions.pipe(
      ofType(CatalogPlanActions.refreshTopViewMinimapAvailabilities),
      withLatestFrom(
        this.store.pipe(select(fromCatalogPlan.selectors.selectBlockId)),
        this.store.pipe(select(fromCatalogPlan.selectors.applyPlanFilter)),
      ),
      filter(([, blockId]) => !!blockId),
      switchMap(([, , planFilter]) =>
        this.applyTopViewFilter(planFilter).pipe(
          concatMap((data: EventSessionPlanBlockFilterResponseDto[]) => {
            this.topViewService.refreshBlockAvailabilities(data);
            return [{ type: 'Empty Action' }];
          }),
          catchError(() => of({ type: 'EMPTY' })),
        ),
      ),
    ),
  );

  private applyTopViewMultiQuickFilter(
    eventSessionId: number,
    multiQuickFilter: MultiQuickFilterSelection,
    seatStatuses?: string[],
  ): Observable<EventSessionPlanBlockFilterResponseDto[]> {
    const contingentIds = multiQuickFilter.selectedContingents?.map((c) => c.id) || [];
    const categoryIds = multiQuickFilter.selectedCategories?.map((c) => c.id) || [];
    const unavailabilityIds = multiQuickFilter.selectedUnavailabilities?.map((u) => u.id) || [];
    // Le statut vient de l'axe quick-filter (onglet Statut) s'il est selectionne, sinon on retombe
    // sur le statut du filtre global (dropdown) passe en fallback.
    const statusIds = multiQuickFilter.selectedStatuses?.length
      ? multiQuickFilter.selectedStatuses.map((s) => s.id)
      : seatStatuses;
    const blockFilterRequest = {
      eventSessionId,
      contingentIds,
      categoryIds,
      unavailableTypes: unavailabilityIds,
      seatStatuses: statusIds?.length ? statusIds : undefined,
    } as EventSessionPlanBlockFilterRequestDto;
    return this.service.applyTopViewMultiQuickFilter(blockFilterRequest);
  }
  private applyTopViewFilter(filter: {
    eventSessionId: number;
    contingentsFilter: number[];
    categoriesFilter: number[];
    unavailableSeatTypesFilter: number[];
    seatStatusesFilter?: string[];
  }) {
    return this.service.applyTopViewFilter(
      filter.eventSessionId,
      filter.contingentsFilter,
      filter.categoriesFilter,
      filter.unavailableSeatTypesFilter,
      filter.seatStatusesFilter,
    );
  }

  private refreshBlockAvailableSeats(
    blockData: EventSessionPlanBlockResponseDto,
    filteredBlocks: EventSessionPlanBlockFilterResponseDto[],
  ) {
    const availableSeats =
      filteredBlocks.find((currentFilteredBlock) => currentFilteredBlock.id === blockData.id)
        ?.availableSeats || 0;

    return {
      ...blockData,
      availableSeats,
    };
  }
}
